import { memo, useEffect, useRef } from 'react';
import { Box2, Vector2, Vector2Tuple } from 'three';

import { CommanderActionTypesEnum } from '@spazious/dollhouse-commander';
import { FNode, FNodeTypesEnum, useGraph } from '@spazious/dollhouse-fsetup';
import { FShallowNode, FShallowNodeTypesEnum } from '@spazious/dollhouse-shallow-graph';
import { useStaging, useStore } from '@spazious/dollhouse-store';
import { DOLLHOUSE_LAYER_DEFAULT } from '@spazious/dollhouse-three';
import { DrawCamerapathStateEnum, ToolModeEnum } from '@spazious/dollhouse-types';
import { useEventCallback } from '@spazious/react-hooks';

import { DollhouseMessage, MainViewMessage } from '../../core/index.js';
import { useDelayedSelection, useDrawCamerapath } from '../../hooks/index.js';
import { useEvents } from '../EventManager/index.js';
import { DrawPolygonEvent, PolygonDrawControls } from '../PolygonDrawControls/index.js';

const _box = new Box2();
const _center = new Vector2();
const _va = new Vector2();
const defaultEyeHeight = 1.81;
const toCameraPathPoints = (points: Vector2Tuple[], eyeHeight: number) => {
  _box.makeEmpty();
  points.forEach((p) => {
    _box.expandByPoint(_va.set(...p));
  });
  _box.getCenter(_center);
  return points.map((p) => {
    return [
      [p[0], eyeHeight, p[1]],
      [_center.x, 0, _center.y],
    ];
  });
};

const CameraPathControlsImpl = memo(() => {
  const events = useEvents();
  const graph = useStore(({ graph }) => graph);
  const world = useStore(({ world }) => world);
  const { staging, createAction } = useStaging();
  const { active, state, set: setCamerapath } = useDrawCamerapath();
  const pointsRef = useRef<Vector2Tuple[]>([]);
  const pendingFinishDrawing = useRef(false);
  const { setSelectors, resetSelection } = useDelayedSelection(() => {
    if (pendingFinishDrawing.current) {
      pendingFinishDrawing.current = false;
      setCamerapath({ active: false, state: DrawCamerapathStateEnum.Initial });
    }
  }, DOLLHOUSE_LAYER_DEFAULT);
  useGraph();

  const getExistingNode = useEventCallback((): FNode | null => {
    let existingNode: FNode | null = null;
    graph.traverse((node) => {
      if (node.type === FNodeTypesEnum.CameraPath) {
        existingNode = node;
      }
    });
    return existingNode;
  });

  useEffect(() => {
    const existingNode = getExistingNode();
    if (existingNode) {
      setSelectors([existingNode.selector]);
    }
    return () => {
      resetSelection();
    };
  }, [getExistingNode, setSelectors, resetSelection]);

  useEffect(() => {
    const cb = (message: MainViewMessage) => {
      if (message === MainViewMessage.CameraPathResetCancel) {
        if (staging.stack.length) {
          staging.abort();
        }
        setCamerapath({ active: false, state: DrawCamerapathStateEnum.Initial });
        pointsRef.current = [];
      } else if (message === MainViewMessage.CameraPathResetConfirm) {
        const existingNode = getExistingNode();
        if (existingNode) {
          staging.add(createAction('remove', existingNode!.selector));
        }
      }
    };
    if (active) {
      events.on('mainViewMessage', cb);
      return () => {
        events.off('mainViewMessage', cb);
      };
    }
  }, [active, createAction, events, getExistingNode, setCamerapath, staging]);

  useEffect(() => {
    if (state === DrawCamerapathStateEnum.Done) {
      if (pointsRef.current.length > 2) {
        let eyeHeight = defaultEyeHeight;
        const wBounds = world?.bounds;
        if (wBounds) {
          eyeHeight = Math.max(Math.round((wBounds.max.y + wBounds.min.y) / 2), defaultEyeHeight);
        }
        const action = createAction('add', {
          type: FShallowNodeTypesEnum.CameraPath,
          points: toCameraPathPoints(pointsRef.current, eyeHeight),
        } as FShallowNode);
        staging.add(action);
        staging.commit(CommanderActionTypesEnum.Add);
        pendingFinishDrawing.current = true;
        setSelectors([action.payload.selector]);
      } else {
        setCamerapath({ active: false, state: DrawCamerapathStateEnum.Initial });
      }
    } else if (state === DrawCamerapathStateEnum.Cancel) {
      if (staging.stack.length) {
        staging.abort();
      }
      pointsRef.current = [];
      setCamerapath({ active: false, state: DrawCamerapathStateEnum.Initial });
    }
  }, [state, staging, setCamerapath, createAction, setSelectors]);

  const onDrawPolygon = useEventCallback((event: DrawPolygonEvent) => {
    const { points, originalEvent } = event;

    switch (originalEvent.type) {
      case 'pointerdown':
        if (points.length === 1) {
          // starting the polygon
          staging.prepare(true);
          setCamerapath({ state: DrawCamerapathStateEnum.Drawing });
          pointsRef.current = [...points];

          const existingNode = getExistingNode();
          if (existingNode) {
            events.emit('dollhouseMessage', DollhouseMessage.ConfirmCameraPathReset);
          }
        } else {
          pointsRef.current = [...points];
        }
        break;

      case 'pointermove':
      case 'pointerup':
        break;

      default:
        break;
    }
  });

  return active ? <PolygonDrawControls fill={false} onDraw={onDrawPolygon} /> : null;
});

export const CameraPathControls = memo(() => {
  const tool = useStore(({ tool }) => tool);
  return tool === ToolModeEnum.CameraPath ? <CameraPathControlsImpl /> : null;
});
